import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X, MessageSquare } from 'lucide-react';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';

/**
 * FeedbackPopup — popup contextuel d'invitation à donner son avis.
 *
 * Critères d'affichage (vérifiés serveur via GET /user/feedback.php → should_show_popup) :
 *  - feedback_popup_enabled = 1 dans app_settings (master switch)
 *  - L'user a complété ≥ 5 sessions (configurable)
 *  - L'user n'a JAMAIS soumis de feedback
 *  - Pas de dismiss récent (snooze 7j) et < 3 dismiss totaux
 *
 * Actions :
 *  - "Donner mon avis" → ouvre le bouton flottant feedback (event custom)
 *  - "Plus tard" → dismiss simple (incrémente compteur)
 *  - "Ne plus afficher" → permanent_dismiss
 *
 * Routes ignorées :
 *  - /gestion/* (admin)
 *  - /quiz/* en cours (transient state)
 *  - / et /connexion (public)
 *
 * Délai d'affichage : 3s après mount pour éviter le pop intempestif au chargement.
 */
export default function FeedbackPopup() {
    const { user } = useAuthStore();
    const location = useLocation();
    const [eligible, setEligible] = useState(false);
    const [visible, setVisible] = useState(false);
    const [dismissing, setDismissing] = useState(false);

    // Routes où on ne montre jamais
    const isExcludedRoute = !user
        || location.pathname.startsWith('/gestion')
        || location.pathname.startsWith('/quiz/')
        || location.pathname.match(/\/quiz\/[^/]+\/quiz\//)
        || location.pathname === '/'
        || location.pathname === '/connexion'
        || location.pathname === '/inscription'
        // Retour de paiement Stripe : la personne doit voir la confirmation d'achat, pas une demande d'avis.
        || /[?&]session_id=/.test(location.search);

    // Check serveur si on doit afficher
    useEffect(() => {
        if (isExcludedRoute) { setEligible(false); return; }
        api.get('/user/feedback.php')
            .then(({ data }) => setEligible(!!data.data?.should_show_popup))
            .catch(() => setEligible(false));
    }, [user, location.pathname, location.search, isExcludedRoute]);

    // Délai de 3s avant affichage pour éviter le pop intempestif
    useEffect(() => {
        if (!eligible) { setVisible(false); return; }
        const t = setTimeout(() => setVisible(true), 3000);
        return () => clearTimeout(t);
    }, [eligible]);

    const dismiss = async (permanent = false) => {
        setDismissing(true);
        try {
            await api.post('/user/feedback.php', { action: 'dismiss_popup', permanent });
        } catch { /* silent */ }
        setVisible(false);
        setEligible(false);
        setDismissing(false);
    };

    const openFeedback = () => {
        // Dispatch un event que le FeedbackButton écoutera pour s'auto-ouvrir
        window.dispatchEvent(new CustomEvent('feedback:open'));
        // Compter ce clic comme un "non-dismiss" et fermer le popup
        setVisible(false);
        setEligible(false);
    };

    if (!visible) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 9050,  // sous le bouton flottant (9000) mais au-dessus du reste
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
            }}
            onClick={() => dismiss(false)}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--card-bg, #1e293b)',
                    color: 'var(--text, #e2e8f0)',
                    border: '1px solid var(--border, #334155)',
                    borderRadius: 14,
                    padding: 28,
                    maxWidth: 440,
                    width: '100%',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                    position: 'relative',
                    textAlign: 'center',
                }}
            >
                <button
                    onClick={() => dismiss(false)}
                    disabled={dismissing}
                    style={{
                        position: 'absolute', top: 12, right: 12,
                        background: 'transparent', border: 'none',
                        color: 'var(--text-muted)', cursor: 'pointer', padding: 6,
                    }}
                    title="Fermer (plus tard)"
                >
                    <X size={18} />
                </button>

                <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(124,58,237,0.25), rgba(37,99,235,0.25))',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 12,
                }}>
                    <Sparkles size={28} style={{ color: '#a78bfa' }} />
                </div>

                <h3 style={{ margin: '0 0 8px', fontSize: '1.15em' }}>
                    Tu utilises monquiz depuis plusieurs sessions !
                </h3>
                <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: '0.9em', lineHeight: 1.5 }}>
                    Ton avis m'intéresse vraiment pour améliorer l'outil. Ça prend 2 minutes max
                    et tu peux passer toutes les questions qui ne te concernent pas.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                        onClick={openFeedback}
                        disabled={dismissing}
                        style={{
                            background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
                            color: 'white', border: 'none',
                            padding: '10px 20px', borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: '0.95em', fontWeight: 600,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}
                    >
                        <MessageSquare size={16} /> Donner mon avis
                    </button>
                    <button
                        onClick={() => dismiss(false)}
                        disabled={dismissing}
                        style={{
                            background: 'transparent', color: 'var(--text-muted)',
                            border: '1px solid var(--border, #334155)',
                            padding: '8px 16px', borderRadius: 8,
                            cursor: 'pointer', fontSize: '0.9em',
                        }}
                    >
                        Plus tard
                    </button>
                    <button
                        onClick={() => dismiss(true)}
                        disabled={dismissing}
                        style={{
                            background: 'transparent', color: 'var(--text-muted)',
                            border: 'none',
                            padding: '4px 8px',
                            cursor: 'pointer', fontSize: '0.78em',
                            textDecoration: 'underline',
                            opacity: 0.7,
                        }}
                    >
                        Ne plus me demander
                    </button>
                </div>
            </div>
        </div>
    );
}
